import React, { useCallback, useState } from 'react';
import { Share2, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CARD_W = 1080;
const CARD_H = 1920;
const LOGO_URL = 'https://jszqmvsvkasqsnxkhqor.supabase.co/storage/v1/object/public/playground-photos/zno-logo.png';

const EVENT_LABELS = {
  created: 'Nouveau terrain',
  captured: 'Terrain capturé',
  defended: 'Terrain défendu',
  fallen_no_defense: 'Terrain tombé',
  record: 'Nouveau record',
};

const EVENT_COLORS = {
  created: '#22c55e',
  captured: '#ef4444',
  defended: '#3b82f6',
  fallen_no_defense: '#f59e0b',
  record: '#a855f7',
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let curY = y;
  for (const word of words) {
    const test = line + (line ? ' ' : '') + word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, curY);
      line = word;
      curY += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, curY);
  return curY;
}

async function renderCard({ event_type, playground_name, playground_city, user_name, opponent_name, score_attacker, score_defender, sponsor }) {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  // Background
  const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
  grad.addColorStop(0, '#0A0B10');
  grad.addColorStop(0.5, '#13151D');
  grad.addColorStop(1, '#0A0B10');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Accent glow
  const accentColor = EVENT_COLORS[event_type] || '#6E2BFF';
  ctx.save();
  ctx.globalAlpha = 0.15;
  const glowGrad = ctx.createRadialGradient(CARD_W / 2, CARD_H * 0.4, 0, CARD_W / 2, CARD_H * 0.4, 500);
  glowGrad.addColorStop(0, accentColor);
  glowGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.restore();

  // Event type badge
  const label = (EVENT_LABELS[event_type] || event_type).toUpperCase();
  ctx.font = 'bold 42px -apple-system, BlinkMacSystemFont, sans-serif';
  const badgeW = ctx.measureText(label).width + 80;
  const badgeH = 80;
  const badgeX = (CARD_W - badgeW) / 2;
  const badgeY = 520;
  ctx.fillStyle = accentColor + '30';
  roundRect(ctx, badgeX, badgeY, badgeW, badgeH, badgeH / 2);
  ctx.fill();
  ctx.fillStyle = accentColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, CARD_W / 2, badgeY + badgeH / 2);

  // Playground city + name on same line: "PARIS • MMM"
  const nameStr = (playground_name || 'Terrain').toUpperCase();
  const cityStr = playground_city ? playground_city.toUpperCase() : null;
  const displayName = cityStr ? `${cityStr}  •  ${nameStr}` : nameStr;

  ctx.font = 'bold 72px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  if (cityStr) {
    // Draw with city in gray and name in white
    const fullText = `${cityStr}  •  ${nameStr}`;
    const fullW = ctx.measureText(fullText).width;
    const cityPart = `${cityStr}  •  `;
    const cityW = ctx.measureText(cityPart).width;
    const startX = (CARD_W - fullW) / 2;

    // If it fits on one line
    if (fullW <= CARD_W - 120) {
      ctx.fillStyle = '#9CA0AB';
      ctx.textAlign = 'left';
      ctx.fillText(cityPart, startX, 660);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(nameStr, startX + cityW, 660);
    } else {
      // Too long — stack them
      ctx.fillStyle = '#9CA0AB';
      ctx.textAlign = 'center';
      ctx.fillText(cityStr, CARD_W / 2, 660);
      ctx.fillStyle = '#FFFFFF';
      wrapText(ctx, nameStr, CARD_W / 2, 750, CARD_W - 120, 85);
    }
  } else {
    wrapText(ctx, nameStr, CARD_W / 2, 660, CARD_W - 120, 85);
  }

  // Score (if applicable)
  let nextY = 880;
  if (score_attacker != null && score_defender != null) {
    ctx.font = 'bold 140px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${score_attacker} - ${score_defender}`, CARD_W / 2, nextY);
    nextY += 180;
  }

  // Player name (uppercase)
  if (user_name) {
    ctx.font = 'bold 52px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = accentColor;
    ctx.textAlign = 'center';
    ctx.fillText(user_name.toUpperCase(), CARD_W / 2, nextY);
    nextY += 70;
  }

  // vs opponent (uppercase)
  if (opponent_name) {
    ctx.font = '40px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#9CA0AB';
    ctx.textAlign = 'center';
    ctx.fillText(`vs ${opponent_name.toUpperCase()}`, CARD_W / 2, nextY);
    nextY += 60;
  }

  // Sponsor
  if (sponsor) {
    ctx.font = '28px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#6B7280';
    ctx.textAlign = 'center';
    ctx.fillText(`Sponsorisé par ${sponsor}`, CARD_W / 2, CARD_H - 260);
  }

  // Watermark: ZNO logo
  try {
    const logo = await loadImage(LOGO_URL);
    const logoH = 80;
    const logoW = (logo.width / logo.height) * logoH;
    ctx.drawImage(logo, (CARD_W - logoW) / 2, CARD_H - 180, logoW, logoH);
  } catch {
    // Fallback text watermark
    ctx.font = 'bold 48px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.globalAlpha = 0.6;
    ctx.textAlign = 'center';
    ctx.fillText('ZNO', CARD_W / 2, CARD_H - 140);
    ctx.globalAlpha = 1;
  }

  // Bottom tagline
  ctx.font = '24px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#4B5563';
  ctx.textAlign = 'center';
  ctx.fillText('app.zno.games', CARD_W / 2, CARD_H - 80);

  return canvas;
}

async function shareOrDownload(canvas) {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  const file = new File([blob], 'zno-share.png', { type: 'image/png' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
    }
  }

  // Fallback: download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'zno-share.png';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return 'downloaded';
}

/**
 * ShareCard button — renders a 1080x1920 image and shares/downloads it.
 * Props: event_type, playground_name, user_name, opponent_name, score_attacker, score_defender, sponsor
 * Optional: className, variant, size, label
 */
export default function ShareCard({ event_type, playground_name, playground_city, user_name, opponent_name, score_attacker, score_defender, sponsor, className, variant = 'ghost', size = 'sm', label }) {
  const [loading, setLoading] = useState(false);

  const handleShare = useCallback(async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const canvas = await renderCard({ event_type, playground_name, playground_city, user_name, opponent_name, score_attacker, score_defender, sponsor });
      await shareOrDownload(canvas);
    } catch (err) {
      console.error('ShareCard error:', err);
    } finally {
      setLoading(false);
    }
  }, [event_type, playground_name, playground_city, user_name, opponent_name, score_attacker, score_defender, sponsor]);

  return (
    <Button variant={variant} size={size} onClick={handleShare} disabled={loading} className={className}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
      {label && <span className="ml-1.5">{label}</span>}
    </Button>
  );
}

export { renderCard, shareOrDownload };
